import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogNavigation from './components/BlogNavigation';
import BlogCard from './components/BlogCard';
import { blogPosts, categories } from '../../mocks/blogPosts';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.title = 'Blog — Career Insights & Job Search Guides | JobOS';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Actionable career advice, AI tool tutorials, and job search strategies from the JobOS team. Free guides on ATS autofill, resume tailoring, and landing offers faster.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'JobOS Blog — Career Insights & Job Search Guides', true);
    setMeta('og:description', 'Actionable advice, proven strategies, and expert tips to help you land your next role faster.', true);
    setMeta('og:url', `${SITE_URL}/blog`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog`);

    document.querySelectorAll('script[data-schema-page="blog"]').forEach(el => el.remove());

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'JobOS Blog',
      url: `${SITE_URL}/blog`,
      description: 'Career insights, AI tool tutorials, and job search guides from the JobOS team.',
      publisher: {
        '@type': 'Organization',
        name: 'JobOS',
        url: SITE_URL,
      },
      blogPost: blogPosts.slice(0, 10).map(post => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        url: `${SITE_URL}/blog/${post.slug}`,
        datePublished: post.date,
        author: {
          '@type': 'Person',
          name: post.author.name,
        },
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema-page', 'blog');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema-page="blog"]').forEach(el => el.remove());
    };
  }, []);

  const featuredPosts = blogPosts.filter((p) => p.featured);
  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch =
      searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const nonFeaturedFiltered = filteredPosts.filter((p) => !p.featured || activeCategory !== 'All' || searchQuery !== '');

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A]">
      <BlogNavigation />

      {/* Hero */}
      <section className="pt-36 pb-16 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[1280px] mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-full text-[#818CF8] text-sm font-medium mb-6">
            <span>✦</span>
            <span>JobOS Blog &amp; Knowledge Base</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight">
            Career Insights &amp;<br />
            <span className="bg-gradient-to-r from-[#818CF8] to-[#6366F1] bg-clip-text text-transparent">
              Job Search Guides
            </span>
          </h1>
          <p className="text-gray-500 dark:text-[#94A3B8] text-xl max-w-[600px] mx-auto mb-10 leading-relaxed">
            Actionable advice, proven strategies, and expert tips to help you land your next role faster.
          </p>

          {/* Search */}
          <div className="relative max-w-[520px] mx-auto">
            <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#64748B]">
              <i className="ri-search-line text-lg"></i>
            </div>
            <input
              type="text"
              placeholder="Search articles, topics, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-12 pl-11 pr-4 bg-gray-50 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#475569] text-sm focus:outline-none focus:border-[#6366F1] transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-10 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#6366F1] text-white shadow-lg shadow-[#6366F1]/20'
                    : 'bg-gray-100 dark:bg-[#141828] text-gray-600 dark:text-[#94A3B8] border border-gray-200 dark:border-[#2D3148] hover:border-[#6366F1]/50 hover:text-[#6366F1]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {activeCategory === 'All' && searchQuery === '' && featuredPosts.length > 0 && (
        <section className="pb-16 bg-white dark:bg-[#0A0E1A]">
          <div className="max-w-[1280px] mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Featured Articles</h2>
            <div className="flex flex-col gap-6">
              {featuredPosts.map((post) => (
                <BlogCard key={post.slug} post={post} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section className="pb-24 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[1280px] mx-auto px-6">
          {(activeCategory !== 'All' || searchQuery !== '') ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'Article' : 'Articles'}
                  {activeCategory !== 'All' && <span className="text-[#6366F1]"> in {activeCategory}</span>}
                  {searchQuery && <span className="text-gray-400 dark:text-[#64748B] text-lg font-normal"> for &quot;{searchQuery}&quot;</span>}
                </h2>
                {(activeCategory !== 'All' || searchQuery !== '') && (
                  <button
                    onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}
                    className="text-sm text-gray-400 dark:text-[#64748B] hover:text-[#6366F1] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i> Clear filters
                  </button>
                )}
              </div>
              {filteredPosts.length === 0 ? (
                <div className="text-center py-24">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 dark:bg-[#141828] rounded-full">
                    <i className="ri-search-line text-2xl text-gray-400 dark:text-[#475569]"></i>
                  </div>
                  <p className="text-gray-500 dark:text-[#94A3B8] text-lg mb-2">No articles found</p>
                  <p className="text-gray-400 dark:text-[#64748B] text-sm">Try a different search term or category</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPosts.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">All Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {nonFeaturedFiltered.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gray-50 dark:bg-[#060A14] border-t border-gray-200 dark:border-[#2D3148]">
        <div className="max-w-[720px] mx-auto px-6 text-center">
          <div className="text-[#818CF8] text-sm font-mono uppercase tracking-wider mb-4 flex items-center justify-center gap-2">
            <span>✦</span><span>READY TO TAKE ACTION?</span>
          </div>
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight">
            Put these strategies to work with JobOS
          </h2>
          <p className="text-gray-500 dark:text-[#94A3B8] text-lg mb-8">
            Track applications, tailor your resume with AI, and autofill every ATS — all in one platform.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-14 px-10 bg-[#6366F1] text-white text-lg font-semibold rounded-full hover:bg-[#4F46E5] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(99,102,241,0.4)] cursor-pointer whitespace-nowrap"
          >
            Get Started Free
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
